import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getContract, acceptContract, declineContract, addMilestone } from "@/services/contracts.service";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtMoney, statusColor, statusLabel } from "@/lib/format";
import { contractCaps } from "@/lib/permissions";
import { Gavel, Plus } from "lucide-react";
import KarmaBadge from "@/components/shared/KarmaBadge";
import MilestoneRow from "@/components/contracts/MilestoneRow";
import ExpandableText from "@/components/shared/ExpandableText";
import { usePolling } from "@/lib/usePolling";

const ContractDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [msTitle, setMsTitle] = useState("");
  const [msAmount, setMsAmount] = useState("");
  const [msDue, setMsDue] = useState("");
  const [msNotes, setMsNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async ({ silent = false } = {}) => {
    try {
      setData(await getContract(id));
    } catch (e) {
      if (!silent) {
        toast.error("Contract not found");
        navigate("/contracts");
      }
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  usePolling(() => load({ silent: true }));

  if (!data) return <div className="text-muted-foreground">Loading…</div>;
  const { contract, milestones, escrow } = data;

  const accept = async () => {
    try { await acceptContract(id); toast.success("Contract accepted"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const decline = async () => {
    try { await declineContract(id); toast.success("Contract declined"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitMilestone = async () => {
    const amt = Number(msAmount);
    if (!msTitle.trim()) { toast.error("Enter a milestone title"); return; }
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try {
      await addMilestone(id, {
        title: msTitle.trim(),
        amount: amt,
        due_date: msDue || undefined,
        notes: msNotes.trim(),
      });
      toast.success("Milestone added");
      setAddOpen(false);
      setMsTitle(""); setMsAmount(""); setMsDue(""); setMsNotes("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add milestone");
    } finally { setSaving(false); }
  };

  const caps = contractCaps(contract);
  const youAre = contract.is_payer && contract.is_payee ? "Both" : contract.is_payer ? "Payer" : contract.is_payee ? "Payee" : "Viewer";

  return (
    <div className="space-y-6" data-testid="contract-detail-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Contract · You are <span className="text-primary">{youAre}</span></div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">{contract.title}</h1>
          <div className="mt-2 text-sm text-muted-foreground flex flex-wrap items-center gap-2">
            <span>{contract.business_name} → {contract.freelancer_name || contract.freelancer_email}</span>
            {contract.freelancer_id && (
              <KarmaBadge userId={contract.freelancer_id} compact />
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusColor(contract.unified_status || contract.status)}`} data-testid="contract-unified-status">{contract.unified_status || statusLabel(contract.status)}</span>
          <div className="font-display font-bold text-2xl">{fmtMoney(contract.total_amount, contract.currency)}</div>
        </div>
      </div>

      {contract.description && (
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Scope</div>
          <ExpandableText text={contract.description} title="Scope" className="mt-2 text-sm leading-relaxed" clamp="line-clamp-4" testid="contract-scope" />
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Reserved Pay Out</div>
          <div className="mt-2 font-display font-bold text-2xl">{fmtMoney(escrow.balance, contract.currency)}</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Milestones</div>
          <div className="mt-2 font-display font-bold text-2xl">{milestones.length}</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Released</div>
          <div className="mt-2 font-display font-bold text-2xl">{milestones.filter((m) => m.status === "Released").length}</div>
        </div>
      </div>

      {contract.status === "Pending" && contract.created_by_user_id !== user?.user_id && (
        <div className="bg-primary/5 border border-primary/30 rounded-xl p-5 flex flex-wrap items-center justify-between gap-3" data-testid="invitation-banner">
          <div>
            <div className="font-display font-semibold">You've been invited</div>
            <div className="text-sm text-muted-foreground">Accept to activate this contract.</div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={decline} data-testid="decline-contract-btn">Decline</Button>
            <Button onClick={accept} data-testid="accept-contract-btn">Accept</Button>
          </div>
        </div>
      )}
      {contract.status === "Pending" && contract.created_by_user_id === user?.user_id && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/40 rounded-xl p-5 text-sm" data-testid="awaiting-acceptance-banner">
          <div className="font-display font-semibold text-amber-900 dark:text-amber-200">Waiting on the counterparty</div>
          <div className="mt-0.5 text-amber-900/80 dark:text-amber-200/80">
            Invite sent to {contract.is_payer ? (contract.freelancer_name || contract.freelancer_email) : contract.business_name}. Funding and milestone actions unlock once they accept.
          </div>
        </div>
      )}

      {contract.next_action && contract.next_action !== "—" && !contract.unified_status && (
        <div className="bg-accent/40 border border-border rounded-lg px-4 py-2.5 text-sm inline-flex items-center gap-2">
          <Gavel className="w-4 h-4 text-primary" /> Next action: <span className="font-semibold">{contract.next_action}</span>
        </div>
      )}

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display text-xl font-semibold">Milestones</h2>
          {caps.can_add_milestone && (
            <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-milestone-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add milestone
            </Button>
          )}
        </div>
        {milestones.length === 0 ? (
          <div className="text-muted-foreground text-sm bg-card border border-border rounded-xl p-8 text-center">
            No milestones yet.
            {caps.can_add_milestone && (
              <> Add one to define the scope and payment schedule.</>
            )}
          </div>
        ) : (
          milestones.map((m) => <MilestoneRow key={m.milestone_id} m={m} contract={contract} user={user} reload={load} />)
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={(v) => !v && setAddOpen(false)}>
        <DialogContent data-testid="add-milestone-modal">
          <DialogHeader><DialogTitle>Add milestone</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {contract.status === "Pending" && (
              <p className="text-sm text-muted-foreground">
                This contract is awaiting acceptance. Milestones you add now are part of what the counterparty reviews before accepting.
              </p>
            )}
            <div>
              <Label>Title *</Label>
              <Input value={msTitle} onChange={(e) => setMsTitle(e.target.value)} placeholder="e.g. Design mockups" className="mt-1.5" data-testid="add-ms-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount (USD) *</Label>
                <Input type="number" min="0" step="0.01" value={msAmount} onChange={(e) => setMsAmount(e.target.value)} placeholder="0.00" className="mt-1.5" data-testid="add-ms-amount-input" />
              </div>
              <div>
                <Label>Due date</Label>
                <Input type="date" value={msDue} onChange={(e) => setMsDue(e.target.value)} className="mt-1.5" data-testid="add-ms-due-input" />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={msNotes} onChange={(e) => setMsNotes(e.target.value)} rows={3} placeholder="Deliverables, acceptance criteria…" className="mt-1.5" data-testid="add-ms-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={submitMilestone} disabled={saving} data-testid="add-ms-submit-btn">{saving ? "Adding…" : "Add milestone"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractDetail;
